import { Controller } from "@hotwired/stimulus"

const SIGN_IN_REQUIRED_MODAL_ID = "signInRequiredModal"
const LIMITED_PERMISSIONS_MODAL_ID = "limitedPermissionsModal"
export default class extends Controller {
  static targets = [
    "term", "startDate", "endDate", "courseSearch", "courseList", "selectedCourses", "caseSearch", "caseList",
    "filterPills", "selectedStudentsField", "viewResultsButton", "sortColumn", "sortDirection",
    "studentSearch", "studentList", "caseOrScriptSearch", "caseOrScriptList", "selectedCases",
    "selectedMetadata", "selectedAssessmentIds", "calibratePerformanceCheckboxes", "completionStatusCheckboxes",
    "selectedCalibratePerformance", "selectedCompletionStatus", "courseCheckboxes","searchInput", "singleSelectCourseList",
    "caseCheckboxes", "saveReportName", "saveReportButton", "singleSelectCaseList", "addDataBtn", "addAssessmentsBtn"
  ]
  static values = {
    url: String,
    page: Number,
    sortColumn: String,
    sortDirection: String,
    signedIn: String,
    limitedAccess: Boolean,
    exportCsvUrl: String,
    exportXlsxUrl: String,
    exportQueuedModalId: { type: String, default: "exportQueuedModal" }
  }

  initialize() {
    // Initialize selected course list for UI state
    this.selectedCourseIds = []
    this.selectedPerformance = []
    this.selectedCompletionStatus = []
    this.selectedCaseIds = []
    this.populateDropdowns()
  }

  populateDropdowns() {
    if (this.hasCalibratePerformanceCheckboxesTarget) {
      this.calibratePerformanceCheckboxesTargets.forEach(cb => {
        if (cb.checked) {
          this.selectedPerformance.push(cb.value);
          this.addPill(cb.value, cb.nextElementSibling?.innerText || cb.value, "performance");
        }
      });
    }
    if (this.hasCompletionStatusCheckboxesTarget) {
      this.completionStatusCheckboxesTargets.forEach(cb => {
        if (cb.checked) {
          this.selectedCompletionStatus.push(cb.value);
          this.addPill(cb.value, cb.nextElementSibling?.innerText || cb.value, "completion");
        }
      });
    }

    if (this.hasCourseCheckboxesTarget) {
      this.courseCheckboxesTargets.forEach(cb => {
        if (cb.checked) {
          const courseId = cb.dataset.courseId
          const courseName = cb.dataset.courseName
          if (!this.selectedCourseIds.includes(courseId)) {
            this.selectedCourseIds.push(courseId)
            this.addPill(courseId, courseName, "course")
            this.removeDocumentSetIdFromUrl()
          }
        }
      })
    }

    if (this.hasCaseCheckboxesTarget) {
      this.caseCheckboxesTargets.forEach(cb => {
        if (cb.checked) {
          const caseId = cb.dataset.caseId
          const caseName = cb.dataset.caseName
          if (!this.selectedCaseIds.includes(caseId)) {
            this.selectedCaseIds.push(caseId)
            this.addPill(caseId, caseName, "case")
          }
        }
      })
    }

    if (this.hasSelectedStudentsFieldTarget) {
      const studentIds = this.selectedStudentsFieldTarget.value
        .split(",")
        .map(id => id.trim())
        .filter(Boolean);

      if (studentIds.length) {
        studentIds.forEach(id => {
          const row = this.element.querySelector(`tr[data-student-id="${id}"]`);
          if (row) {
            const checkbox = row.querySelector('input[type="checkbox"]');
            if (checkbox && !checkbox.checked) {
              checkbox.checked = true;
            }
          }
        });
        this.updateViewResultsButton();
        this.addCustomStudentFilterPill();
      }
    }
    this.toggleClearFiltersButton();
  }

  // Remove document_set_id from the URL
  removeDocumentSetIdFromUrl() {
    const url = new URL(window.location);
    if (url.searchParams.has('document_set_id')) {
      url.searchParams.delete('document_set_id');
      window.history.replaceState({}, '', url);
    }
  }

  connect() {
    // Setup on page load
    this.debounceTimer = null
    this.normalizeDateInputValue(this.startDateTarget)
    this.normalizeDateInputValue(this.endDateTarget)
    this.initializeDatePickers()
    this.attachPaginationListeners()
    if (this.startDateTarget.value && this.endDateTarget.value) {
      const startDate = this.parseReportFilterDate(this.startDateTarget.value)
      if (startDate) {
        $(this.endDateTarget).datepicker("option", "minDate", startDate)
      }
    } else {
      this.endDateTarget.disabled = true
    }
    this._handleEscape = this._handleEscape.bind(this)
    this.addEventListeners()
    this.showInitialLoader()
    this.previousValue = this.termTarget.value.trim()
    this.toggleSaveButton()
    this.setupTabUrlParamSync();
    this.attachCollapseRowListeners();
  }

  disconnect() {
    // Clean up event listeners when controller is disconnected
    this.removeEventListeners()
    this.hideLoader()
  }

  addEventListeners() {
    const frame = document.getElementById("new_report_page")
    if (frame) {
      frame.addEventListener("turbo:before-fetch-request", this.showLoader.bind(this))
      frame.addEventListener("turbo:before-fetch-response", this.hideLoader.bind(this))
    }
    document.addEventListener("reportTable:replaced", this.onFrameLoad)
    document.addEventListener("students:selected", this.setSelectedStudents.bind(this))
    document.addEventListener("keydown", this._handleEscape)
  }

  removeEventListeners() {
    const frame = document.getElementById("new_report_page")
    if (frame) {
      frame.removeEventListener("turbo:before-fetch-request", this.showLoader.bind(this))
      frame.removeEventListener("turbo:before-fetch-response", this.hideLoader.bind(this))
    }
    document.removeEventListener("reportTable:replaced", this.onFrameLoad)
    document.removeEventListener("students:selected", this.setSelectedStudents.bind(this))
    document.removeEventListener("keydown", this._handleEscape)
  }

  toggleSaveButton() {
    if (!this.hasSaveReportNameTarget || !this.hasSaveReportButtonTarget) return;
    this.saveReportButtonTarget.disabled = this.saveReportNameTarget.value.trim() === "";
  }

  focusSaveInput() {
    if (this.hasSaveReportNameTarget) {
      this.saveReportNameTarget.focus()
    }
  }

  // Debounced search trigger (e.g., for course name)
  search() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.submitFilters(), 100)
  }

  searchStudent() {
    const value = this.termTarget.value.trim()
    if (value === "" && this.previousValue === "") {
      return
    }
    this.previousValue = value
    this.search();
  }

  // Submit all current filters via Turbo
  submitFilters() {
    this.showLoader()
    const url = `${this.urlValue}?${this.buildQueryParams().toString()}`
    fetch(url, {
      headers: { Accept: "text/vnd.turbo-stream.html" }
    }).then(response => response.text())
      .then(html => Turbo.renderStreamMessage(html))
      .catch(error => {
        console.error("Error fetching report:", error)
      });
  }

  // Build query params from current UI values
  buildQueryParams(overrides = {}) {
    const params = new URLSearchParams(window.location.search)

    this.setParamIfPresent(params, "term", this.termTarget)
    this.setParamIfPresent(params, "start_date", this.startDateTarget)
    this.setParamIfPresent(params, "end_date", this.endDateTarget)
    if (this.hasSelectedCoursesTarget) {
      this.setParamIfPresent(params, "selected_courses", this.selectedCoursesTarget)
    }
    if (this.hasSortColumnTarget) {
      this.setParamIfPresent(params, "sort_column", this.sortColumnTarget)
    }
    if (this.hasSortDirectionTarget) {
      this.setParamIfPresent(params, "sort_direction", this.sortDirectionTarget)
    }
    if (this.hasSelectedMetadataTarget) {
      this.selectedMetadataTargets
        .filter(target => target.checked)
        .forEach(target => {
          this.setParamIfPresent(params, "metadata[]", target)
        });
    }
    if (this.hasSelectedAssessmentIdsTarget) {
      this.selectedAssessmentIdsTargets.forEach(target => {
        this.setParamIfPresent(params, "selected_assessment_ids[]", target)
      })
    }
    if (this.hasSelectedStudentsFieldTarget) {
      this.setParamIfPresent(params, "selected_students", this.selectedStudentsFieldTarget)
    }
    if (this.hasSelectedCasesTarget) {
      this.setParamIfPresent(params, "selected_cases", this.selectedCasesTarget)
    }
    if (this.hasSelectedCalibratePerformanceTarget) {
      this.setParamIfPresent(params, "selected_calibrate_performance", this.selectedCalibratePerformanceTarget)
    }
    if (this.hasSelectedCompletionStatusTarget) {
      this.setParamIfPresent(params, "selected_completion_status", this.selectedCompletionStatusTarget)
    }

    // Apply overrides (e.g., page, per_page)
    Object.entries(overrides).forEach(([key, value]) => {
      params.set(key, value)
    })

    return params
  }

  // Helper to add a param if a value is present in a field
  setParamIfPresent(params, key, target) {
    if (target && typeof target.value === "string" && target.value.trim() !== "") {
      let value = target.value.trim();
      if (key.endsWith("[]")) {
        params.append(key, value);
      } else {
        params.set(key, value);
      }
    }
  }

  // Navigate to a specific page
  goToPage(page) {
    this.showLoader()
    const params = this.buildQueryParams({ page, per_page: this.getCurrentPerPage() })
    const url = `${this.urlValue}?${params.toString()}`
    Turbo.visit(url, { frame: "report_table" })
  }

  // Get current per-page value from dropdown
  getCurrentPerPage() {
    const perPageSelect = document.querySelector(".select-per-page")
    return perPageSelect ? parseInt(perPageSelect.value, 10) : 10
  }

  // Change per-page limit and reset to page 1
  changePerPage(perPage) {
    this.showLoader()
    const params = this.buildQueryParams({ page: 1, per_page: perPage })
    this.perPage = perPage
    const url = `${this.urlValue}?${params.toString()}`
    Turbo.visit(url, { frame: "report_table" })
  }

  defaultFirstDay() {
    return new Date(2025, 6, 1)
  }

  // Initialize jQuery datepickers
  initializeDatePickers() {
    const FIRST_DAY = this.defaultFirstDay()
    const TODAY = new Date();

    const startDateOptions = {
      numberOfMonths: 1,
      dateFormat: "mm-dd-yy",
      onSelect: (dateText) => this.handleStartDateSelected(dateText)
    };

    const endDateOptions = {
      numberOfMonths: 1,
      dateFormat: "mm-dd-yy",
      onSelect: (dateText) => this.handleEndDateSelected(dateText)
    };

    // Always initialize datepickers so users can pick dates
    $(this.startDateTarget)
      .datepicker(startDateOptions);

    $(this.endDateTarget)
      .datepicker(endDateOptions);

    // Only set default dates if not student or case report
    const isStudentOrCaseReport = ["student", "case"].includes(this.reportType);

    if (!isStudentOrCaseReport) {
      if (!this.startDateTarget.value) {
        this.startDateTarget.value = $.datepicker.formatDate("mm-dd-yy", FIRST_DAY);
      }
      if (!this.endDateTarget.value) {
        this.endDateTarget.value = $.datepicker.formatDate("mm-dd-yy", TODAY);
      }
    }
  }

  get reportType() {
    return this.element.dataset.reportType;
  }

  parseReportFilterDate(dateText) {
    if (!dateText || typeof dateText !== "string") return null

    const trimmed = dateText.trim()
    if (!trimmed) return null

    try {
      const parsed = $.datepicker.parseDate("mm-dd-yy", trimmed)
      if (parsed) return parsed
    } catch (_error) {
      // fall through to ISO
    }

    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!isoMatch) return null

    const year = Number(isoMatch[1])
    const month = Number(isoMatch[2])
    const day = Number(isoMatch[3])
    const dateObj = new Date(year, month - 1, day)
    if (
      Number.isNaN(dateObj.getTime()) ||
      dateObj.getFullYear() !== year ||
      dateObj.getMonth() !== month - 1 ||
      dateObj.getDate() !== day
    ) {
      return null
    }

    return dateObj
  }

  normalizeDateInputValue(target) {
    if (!target?.value) return

    const parsed = this.parseReportFilterDate(target.value)
    if (!parsed) return

    target.value = $.datepicker.formatDate("mm-dd-yy", parsed)
  }

  handleStartDateSelected(dateText) {
    const startDate = this.parseReportFilterDate(dateText);
    if (startDate) {
      // Enable end date
      this.endDateTarget.disabled = false;
      $(this.endDateTarget).datepicker("option", "minDate", startDate);

      // If end date is empty, set to today's date
      if (!this.endDateTarget.value) {
        const today = new Date();
        const formattedToday = $.datepicker.formatDate("mm-dd-yy", today);
        this.endDateTarget.value = formattedToday;
      }
    } else {
      this.endDateTarget.value = "";
      this.endDateTarget.disabled = true;
    }
    this.search();
  }

  handleEndDateSelected(dateText) {
    const endDate = this.parseReportFilterDate(dateText);
    const startDate = this.parseReportFilterDate(this.startDateTarget.value);

    if (!endDate || endDate < startDate) {
      $(this.endDateTarget).datepicker("setDate", startDate);
    }
    this.search();
  }

  formatDate(event) {
    const target = event.target;
    const isStartDate = target === this.startDateTarget;

    const FIRST_DAY = this.defaultFirstDay()
    const TODAY = new Date();
    const defaultDate = isStartDate ? FIRST_DAY : TODAY;
    const dateFormat = "mm-dd-yy";

    let digits = target.value.replace(/\D/g, "");

    // Auto-insert dashes
    if (digits.length > 2 && digits.length <= 4) {
      target.value = `${digits.substring(0, 2)}-${digits.substring(2)}`;
    } else if (digits.length > 4) {
      target.value = `${digits.substring(0, 2)}-${digits.substring(2, 4)}-${digits.substring(4, 8)}`;
    } else {
      target.value = digits;
    }

    if (event.type === "change") {
      let isValid = false;
      let dateObj;

      if (digits.length === 8) {
        let month = digits.substring(0, 2);
        let day = digits.substring(2, 4);
        let year = parseInt(digits.substring(4, 8), 10);
        dateObj = new Date(`${year}-${month}-${day}`);

        if (
          !isNaN(dateObj.getTime()) &&
          month >= "01" && month <= "12" &&
          day >= "01" && day <= "31" &&
          year > 2000
        ) {
          target.value = `${month}-${day}-${year}`;
          isValid = true;
        }
      }

      if (!isValid) {
        target.value = $.datepicker.formatDate(dateFormat, defaultDate);
        dateObj = defaultDate;
      }

      if (isStartDate) {
        // Enable end date on start date change
        this.endDateTarget.disabled = false;
        $(this.endDateTarget).datepicker("option", "minDate", dateObj);
        // If end date is empty, set to today's date
        if (!this.endDateTarget.value) {
          const today = new Date();
          const formattedToday = $.datepicker.formatDate("mm-dd-yy", today);
          this.endDateTarget.value = formattedToday;
        }
      } else {
        // Ensure end date >= start date
        const startDate = this.parseReportFilterDate(this.startDateTarget.value);
        if (startDate && dateObj < startDate) {
          target.value = $.datepicker.formatDate(dateFormat, startDate);
        }
      }

      this.search();
    }

    // If user is typing in start date, clear end date immediately
    if (isStartDate && event.type === "input") {
      this.endDateTarget.value = "";
      this.endDateTarget.disabled = true;
    }
  }

  handleDateEnter(event) {
    event.preventDefault();

    this.formatDate({
      target: event.target,
      type: "change"
    });
  }

  // Re-attach pagination listeners after Turbo frame reload
  attachPaginationListeners() {
    const prevBtn = document.querySelector(".btn-pagination-prev")
    const nextBtn = document.querySelector(".btn-pagination-next")
    const perPageSelect = document.querySelector(".select-per-page")

    prevBtn?.addEventListener("click", () => {
      const page = parseInt(prevBtn.dataset.page, 10)
      if (page > 0) this.goToPage(page)
    })

    nextBtn?.addEventListener("click", () => {
      const page = parseInt(nextBtn.dataset.page, 10)
      if (!isNaN(page)) this.goToPage(page)
    })

    perPageSelect?.addEventListener("change", (e) => {
      const perPage = parseInt(e.target.value, 10)
      if (!isNaN(perPage)) this.changePerPage(perPage)
    })
  }

  // Called when Turbo Frame loads (used to reattach listeners)
  onFrameLoad = () => {
    this.attachPaginationListeners()
  }

  // Filter courses list in modal dropdown
  filterCourses() {
    const query = this.courseSearchTarget.value.toLowerCase()
    const items = this.courseListTarget.querySelectorAll(".form-check")

    items.forEach((item) => {
      const text = item.textContent.toLowerCase()
      item.style.display = text.includes(query) ? "block" : "none"
    })
  }

  filterCases() {
    const query = this.caseOrScriptSearchTarget.value.toLowerCase();
    const items = this.caseOrScriptListTarget.querySelectorAll(".form-check");
    items.forEach(item => {
      const text = item.textContent.toLowerCase();
      item.style.display = text.includes(query) ? "" : "none";
    });
  }

  filterStudents() {
    const query = this.studentSearchTarget.value.toLowerCase();
    const items = this.studentListTarget.querySelectorAll(".student-dropdown-item");

    items.forEach((item) => {
      const text = item.textContent.toLowerCase();
      item.style.display = text.includes(query) ? "" : "none";
    });
  }

  toggleCaseSelection(event) {
    const checkbox = event.target
    const caseId = checkbox.dataset.caseId
    const caseName = checkbox.dataset.caseName

    if (!this.selectedCaseIds) this.selectedCaseIds = []

    if (checkbox.checked) {
    if (!this.selectedCaseIds.includes(caseId)) {
        this.selectedCaseIds.push(caseId)
        this.addPill(caseId, caseName, "case")
      }
    } else {
      this.selectedCaseIds = this.selectedCaseIds.filter(id => id !== caseId)
      this.removePill(caseId, "case")
    }

    // Update hidden field
    if (this.hasSelectedCasesTarget) {
      this.selectedCasesTarget.value = this.selectedCaseIds.join(",")
    }

    this.submitFilters()
    this.toggleClearFiltersButton()
  }

  // Handle course checkbox toggle
  toggleCourseSelection(event) {
    const checkbox = event.target
    const courseId = checkbox.dataset.courseId
    const courseName = checkbox.dataset.courseName

    if (!this.selectedCourseIds) this.selectedCourseIds = []

    if (checkbox.checked) {
      if (!this.selectedCourseIds.includes(courseId)) {
        this.selectedCourseIds.push(courseId)
        this.addPill(courseId, courseName, "course")
      }
    } else {
      this.selectedCourseIds = this.selectedCourseIds.filter(id => id !== courseId)
      this.removePill(courseId, "course")
    }

    // Sync hidden field
    if (this.hasSelectedCoursesTarget) {
      this.selectedCoursesTarget.value = this.selectedCourseIds.join(",")
    }

    this.submitFilters()
    this.toggleClearFiltersButton()
  }

  togglePerformanceSelection(event) {
    const checkbox = event.target
    const value = checkbox.value
    const label = checkbox.nextElementSibling?.innerText || value

    if (!this.selectedPerformance) this.selectedPerformance = []

    if (checkbox.checked) {
      if (!this.selectedPerformance.includes(value)) {
        this.selectedPerformance.push(value)
        this.addPill(value, label, "performance")
      }
    } else {
      this.selectedPerformance = this.selectedPerformance.filter(v => v !== value)
      this.removePill(value, "performance")
    }

    if (this.hasSelectedCalibratePerformanceTarget) {
      this.selectedCalibratePerformanceTarget.value = this.selectedPerformance.join(",")
    }

    this.submitFilters()
    this.toggleClearFiltersButton()
  }

  // Toggle checkbox selection for Completion Status
  toggleCompletionStatusSelection(event) {
    const checkbox = event.target
    const value = checkbox.value
    const label = checkbox.nextElementSibling?.innerText || value

    if (!this.selectedCompletionStatus) this.selectedCompletionStatus = []

    if (checkbox.checked) {
      if (!this.selectedCompletionStatus.includes(value)) {
        this.selectedCompletionStatus.push(value)
        this.addPill(value, label, "completion")
      }
    } else {
      this.selectedCompletionStatus = this.selectedCompletionStatus.filter(v => v !== value)
      this.removePill(value, "completion")
    }

    if (this.hasSelectedCompletionStatusTarget) {
      this.selectedCompletionStatusTarget.value = this.selectedCompletionStatus.join(",")
    }

    this.submitFilters()
    this.toggleClearFiltersButton()
  }

  onCalibratePerformanceOpen() {
    if (!this.hasCalibratePerformanceCheckboxesTarget) return

    this.calibratePerformanceCheckboxesTargets.forEach(cb => {
      cb.checked = this.selectedPerformance.includes(cb.value)
    })
  }

  onCompletionStatusOpen() {
    if (!this.hasCompletionStatusCheckboxesTarget) return

    this.completionStatusCheckboxesTargets.forEach(cb => {
      cb.checked = this.selectedCompletionStatus.includes(cb.value)
    })
  }

  // Adds a visual pill for selected filters
  addPill(id, label, type) {
    if (!this.hasFilterPillsTarget) return

    const pill = document.createElement("div")
    pill.className = "filter-easy-clear-button p-2"
    pill.dataset[`${type}Id`] = id
    pill.innerHTML = `
      ${label}
      <a href="#" class="text-black ms-1" aria-label="Remove"
        data-pill-type="${type}"
        data-pill-id="${id}">×</a>
    `
    this.filterPillsTarget.appendChild(pill)
  }

  removePill(id, type) {
    if (!this.hasFilterPillsTarget) return
    const pill = this.filterPillsTarget.querySelector(`[data-${type}-id="${id}"]`)
    if (pill) pill.remove()

    let checkbox;
    if (type === "course") {
      checkbox = this.courseListTarget.querySelector(`input[data-course-id="${id}"]`)
    } else if (type === "case" && this.hasCaseOrScriptListTarget) {
      checkbox = this.caseOrScriptListTarget.querySelector(`input[data-case-id="${id}"]`)
    } else {
      checkbox = document.querySelector(`input[type="checkbox"][value="${id}"]`)
    }
    if (checkbox) checkbox.checked = false
  }

  removePillClick(event) {
    // Delegated click from filterPills container (for any pills that didn’t get a direct listener)
    const el = event.target?.nodeType === Node.ELEMENT_NODE ? event.target : event.target?.parentElement
    const link = el?.closest?.("a[data-pill-type]")
    if (!link) return
    event.preventDefault()
    this.removePillForLink(link)
  }

  removePillForLink(link) {
    if (this.isGuestUser()) {
      this.showSignInRequiredModal()
      return
    }
    if (this.isLimitedAccessUser()) {
      this.showLimitedPermissionsModal()
      return
    }

    const type = link.dataset.pillType
    const id = link.dataset.pillId

    switch (type) {
      case "course":
        this.removePill(id, "course")
        this.selectedCourseIds = this.selectedCourseIds.filter(cid => cid !== id)
        this.selectedCoursesTarget.value = this.selectedCourseIds.join(",")
        break
      case "performance":
        this.removePill(id, "performance")
        this.selectedPerformance = this.selectedPerformance.filter(pid => pid !== id)
        this.selectedCalibratePerformanceTarget.value = this.selectedPerformance.join(",")
        break
      case "completion":
        this.removePill(id, "completion")
        this.selectedCompletionStatus = this.selectedCompletionStatus.filter(cid => cid !== id)
        this.selectedCompletionStatusTarget.value = this.selectedCompletionStatus.join(",")
        break
      case "case":
        this.removePill(id, "case")
        this.selectedCaseIds = this.selectedCaseIds.filter(cid => cid !== id)
        if (this.hasSelectedCasesTarget) {
          this.selectedCasesTarget.value = this.selectedCaseIds.join(",")
        }
        break
    }

    this.submitFilters()
    this.toggleClearFiltersButton()
  }

  isGuestUser() {
    if (this.hasSignedInValue) {
      return this.signedInValue !== "true"
    }
    const v = this.element.dataset.reportFilterSignedInValue
    return v === "false"
  }

  isLimitedAccessUser() {
    return this.hasLimitedAccessValue && this.limitedAccessValue === true
  }

  showSignInRequiredModal() {
    const modalEl = document.getElementById(SIGN_IN_REQUIRED_MODAL_ID)
    if (!modalEl) return
    const trigger = document.querySelector(
      `[data-bs-target="#${SIGN_IN_REQUIRED_MODAL_ID}"], [data-target="#${SIGN_IN_REQUIRED_MODAL_ID}"]`
    )
    if (trigger) {
      trigger.click()
      return
    }
    if (typeof bootstrap !== "undefined" && bootstrap.Modal) {
      bootstrap.Modal.getOrCreateInstance(modalEl).show()
      return
    }
    if (typeof $ !== "undefined" && typeof $.fn.modal !== "undefined") {
      $(modalEl).modal("show")
    }
  }

  showLimitedPermissionsModal() {
    const modalEl = document.getElementById(LIMITED_PERMISSIONS_MODAL_ID)
    if (!modalEl) return
    const trigger = document.querySelector(
      `[data-bs-target="#${LIMITED_PERMISSIONS_MODAL_ID}"], [data-target="#${LIMITED_PERMISSIONS_MODAL_ID}"]`
    )
    if (trigger) {
      trigger.click()
      return
    }
    if (typeof bootstrap !== "undefined" && bootstrap.Modal) {
      bootstrap.Modal.getOrCreateInstance(modalEl).show()
      return
    }
    if (typeof $ !== "undefined" && typeof $.fn.modal !== "undefined") {
      $(modalEl).modal("show")
    }
  }

  showExportQueuedModal(message) {
    const modalEl = document.getElementById(this.exportQueuedModalIdValue)
    if (!modalEl) return
    if (message) {
      const msgEl = modalEl.querySelector("[data-export-queued-message]")
      if (msgEl) {
        msgEl.textContent = message
      }
    }
    if (typeof bootstrap !== "undefined" && bootstrap.Modal) {
      bootstrap.Modal.getOrCreateInstance(modalEl).show()
      return
    }
    if (typeof $ !== "undefined" && typeof $.fn.modal !== "undefined") {
      $(modalEl).modal("show")
    }
  }

  // Dismiss export-ready toast appended under this controller (see reports/shared/_export_ready_toast_item).
  hideExportReadyToast(event) {
    const toast = event.currentTarget.closest(".export-ready-toast")
    if (toast) {
      toast.remove()
    }
  }

  // Set selected student IDs from event
  setSelectedStudents(event) {
    const ids = event.detail.selectedStudents;
    if (this.hasSelectedStudentsFieldTarget) {
      this.selectedStudentsFieldTarget.value = ids.join(",");
    }
    this.updateViewResultsButton()
  }

  // Enable/disable view results button based on selected students
  updateViewResultsButton() {
    if (!this.hasViewResultsButtonTarget) return;

    const button = this.viewResultsButtonTarget;
    const selected = this.selectedStudentsFieldTarget?.value?.trim();

    if (selected) {
      button.classList.remove("disabled");
      button.style.pointerEvents = "auto";
    } else {
      button.classList.add("disabled");
      button.style.pointerEvents = "none";
    }
  }

  // Trigger filtering when clicking "View Results"
  viewResults(event) {
    event.preventDefault();
    if (!this.filterPillsTarget.querySelector("#custom-student-pill")) {
      this.addCustomStudentFilterPill();
    }
    this.submitFilters();
  }

  // Show a special pill when custom students are selected
  addCustomStudentFilterPill() {
    if (!this.hasFilterPillsTarget) return;

    const pill = document.createElement("div");
    pill.id = "custom-student-pill";
    pill.className = "filter-easy-clear-button p-2";
    pill.innerHTML = `
      Custom student selection
      <a href="#" class="text-black ms-1" aria-label="Remove student filter"
        data-action="click->report-filter#removeCustomStudentFilter">×</a>
    `;

    this.filterPillsTarget.appendChild(pill);
    this.toggleClearFiltersButton()
  }

  // Clear custom student filter and refresh results
  removeCustomStudentFilter(event) {
    event.preventDefault();

    const pill = this.filterPillsTarget.querySelector("#custom-student-pill");
    if (pill) pill.remove();

    if (this.hasSelectedStudentsFieldTarget) {
      this.selectedStudentsFieldTarget.value = "";
    }
    this.element.dispatchEvent(new CustomEvent("students:reset", { bubbles: true }));
    this.updateViewResultsButton();
    this.submitFilters();
    this.toggleClearFiltersButton()
  }

  // Handle column sorting (e.g., student, assessments, cases)
  sort(event) {
    event.preventDefault();

    const column = event.params.column;
    const direction = event.params.direction;

    if (!column || !direction) return;

    if (this.hasSortColumnTarget) this.sortColumnTarget.value = column;
    if (this.hasSortDirectionTarget) this.sortDirectionTarget.value = direction;

    this.submitFilters();
  }

  exportReport(event) {
    event.preventDefault()

    const format = event.target.dataset.format || "csv"
    if (this.hasExportCsvUrlValue && this.exportCsvUrlValue && format === "csv") {
      this.submitComprehensiveArchiveExport(this.exportCsvUrlValue)
      return
    }
    if (this.hasExportXlsxUrlValue && this.exportXlsxUrlValue && format === "xlsx") {
      this.submitComprehensiveArchiveExport(this.exportXlsxUrlValue)
      return
    }

    const params = this.buildQueryParams({
      format: format,
      export: "true"
    })

    const url = `${this.urlValue}?${params.toString()}`
    window.location.href = url
  }

  async submitComprehensiveArchiveExport(formAction) {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    const params = this.buildQueryParams()
    if (token) {
      params.set("authenticity_token", token)
    }

    try {
      const response = await fetch(formAction, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          ...(token ? { "X-CSRF-Token": token } : {}),
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
        },
        body: params.toString()
      })
      if (response.ok) {
        let message = null
        try {
          const data = await response.json()
          message = data?.message
        } catch {
          // ignore JSON parse errors
        }
        this.showExportQueuedModal(message)
      } else {
        console.error("Export request failed:", response.status)
        window.alert("Export could not be started. Please try again.")
      }
    } catch (error) {
      console.error("Export request error:", error)
      window.alert("Export could not be started. Please try again.")
    }
  }

  clearFilters() {
    if (this.isGuestUser()) {
      this.showSignInRequiredModal()
      return
    }
    if (this.isLimitedAccessUser()) {
      this.showLimitedPermissionsModal()
      return
    }

    // Reset internal filter state
    this.selectedCourseIds = [];
    this.selectedPerformance = [];
    this.selectedCompletionStatus = [];
    this.selectedCaseIds = [];

    // Uncheck all course checkboxes
    if (this.hasCourseListTarget) {
      const checkboxes = this.courseListTarget.querySelectorAll("input[type='checkbox'][data-course-id]");
      checkboxes.forEach(checkbox => checkbox.checked = false);
    }

    // Uncheck all performance checkboxes
    if (this.hasCalibratePerformanceCheckboxesTarget) {
      const checkboxes = this.calibratePerformanceCheckboxesTarget.querySelectorAll("input[type='checkbox']");
      checkboxes.forEach(checkbox => checkbox.checked = false);
    }

    // Uncheck all completion status checkboxes
    if (this.hasCompletionStatusCheckboxesTarget) {
      const checkboxes = this.completionStatusCheckboxesTarget.querySelectorAll("input[type='checkbox']");
      checkboxes.forEach(checkbox => checkbox.checked = false);
    }

    // Uncheck all case checkboxes
    if (this.hasCaseOrScriptListTarget) {
      const checkboxes = this.caseOrScriptListTarget.querySelectorAll("input[type='checkbox'][data-case-id]");
      checkboxes.forEach(checkbox => checkbox.checked = false);
    }

    // Clear hidden fields
    if (this.hasSelectedCoursesTarget) {
      this.selectedCoursesTarget.value = "";
    }

    if (this.hasSelectedCalibratePerformanceTarget) {
      this.selectedCalibratePerformanceTarget.value = "";
    }

    if (this.hasSelectedCompletionStatusTarget) {
      this.selectedCompletionStatusTarget.value = "";
    }

    if (this.hasSelectedStudentsFieldTarget) {
      this.selectedStudentsFieldTarget.value = "";
    }

    if (this.hasSelectedCasesTarget) {
      this.selectedCasesTarget.value = "";
    }

    // Remove all pills (course, performance, completion, student, case)
    if (this.hasFilterPillsTarget) {
      this.filterPillsTarget.innerHTML = "";
    }

    // Dispatch event to reset student modal (if needed)
    this.element.dispatchEvent(new CustomEvent("students:reset", { bubbles: true }));

    // Update view results button
    this.updateViewResultsButton();

    // Submit cleared filters
    this.submitFilters();

    // Hide clear filters button
    this.toggleClearFiltersButton();
  }

  toggleClearFiltersButton() {
    const hasCourses = this.selectedCourseIds.length > 0;
    const hasStudents = this.hasSelectedStudentsFieldTarget && this.selectedStudentsFieldTarget.value.trim() !== "";
    const hasPerformance = this.selectedPerformance.length > 0;
    const hasCompletion = this.selectedCompletionStatus.length > 0;
    const hasCases = this.selectedCaseIds && this.selectedCaseIds.length > 0;

    const shouldShow = hasCourses || hasStudents || hasPerformance || hasCompletion || hasCases;

    const btn = document.getElementById("clear-filters-btn");
    if (btn) {
      btn.classList.toggle("d-none", !shouldShow);
    }
  }

  _handleEscape(event) {
    if (event.key === "Escape") {
      const openMenus = document.querySelectorAll(".dropdown-list:not([hidden]):not(.select-student-dropdown)")
      openMenus.forEach(menu => {
        // close dropdown
        menu.setAttribute("hidden", "")

        // unflip chevron if present
        const button = menu.closest(".dropdown")?.querySelector(".fa-chevron-down")
        if (button?.classList.contains("flipped")) {
          button.classList.remove("flipped")
        }
      })
    }
  }

  showLoader() {
    const loader = document.getElementById("report-loader")
    const reportTable = document.getElementById("report_shared_table")
    if (loader && reportTable) {
      loader.classList.remove("d-none")
      reportTable.classList.add("d-none")
    }
    if (this.hasAddDataBtnTarget) {
      this.addDataBtnTarget.disabled = true
      this.addDataBtnTarget.classList.add("btn-disabled")
    }
    if (this.hasAddAssessmentsBtnTarget) {
      this.addAssessmentsBtnTarget.disabled = true
      this.addAssessmentsBtnTarget.classList.add("btn-disabled")
    }
  }

  // Hide loader
  hideLoader() {
    const loader = document.getElementById("report-loader")
    const reportTable = document.getElementById("report_shared_table")
    if (loader && reportTable) {
      loader.classList.add("d-none")
      reportTable.classList.remove("d-none")
    }
    if (this.hasAddDataBtnTarget) {
      this.addDataBtnTarget.disabled = false
      this.addDataBtnTarget.classList.remove("btn-disabled")
    }
    if (this.hasAddAssessmentsBtnTarget) {
      this.addAssessmentsBtnTarget.disabled = false
      this.addAssessmentsBtnTarget.classList.remove("btn-disabled")
    }
  }

  showInitialLoader() {
    this.showLoader()

    setTimeout(() => {
      this.hideLoader()
    }, 3000)
  }

  filterSingleCourse(event) {
    const query = event.target.value.toLowerCase()
    const links = this.singleSelectCourseListTarget.querySelectorAll("a")

    links.forEach(link => {
      const courseName = link.dataset.courseName || ""
      link.style.display = courseName.includes(query) ? "block" : "none"
    })
  }

  filterSingleCase(event) {
    const query = event.target.value.toLowerCase();
    const links = this.singleSelectCaseListTarget.querySelectorAll("a");

    links.forEach(link => {
      const caseName = link.dataset.caseName || "";
      link.style.display = caseName.includes(query) ? "" : "none";
    });
  }

  setupTabUrlParamSync() {
    const reportTabs = document.getElementById('reportTabs');
    if (!reportTabs) return;

    // Helper to get tabTrigger by param
    const getTabTrigger = (tabParam) => {
      if (!tabParam) return null;
      const tabTarget = `#${tabParam.replace(/_/g, '-')}`;
      return reportTabs.querySelector(`.nav-link[data-bs-target="${tabTarget}"]`);
    };

    // Listen for Bootstrap tab show event and update URL param
    reportTabs.querySelectorAll('.nav-link').forEach(tab => {
      // Remove any previous listener to avoid duplicates
      tab.removeEventListener('shown.bs.tab', tab._tabUrlHandler);
      tab._tabUrlHandler = (event) => {
        const target = event.target.getAttribute('data-bs-target');
        if (target) {
          const tabName = target.replace(/^#/, '').replace(/-/g, '_');
          const url = new URL(window.location);
          url.searchParams.set('tab', tabName);
          history.replaceState(null, '', url);
        }
      };
      tab.addEventListener('shown.bs.tab', tab._tabUrlHandler);
    });

    // On page load, activate tab from ?tab=... if present and valid, else default to system_view
    const url = new URL(window.location);
    let tabParam = url.searchParams.get('tab');
    let tabTrigger = getTabTrigger(tabParam);

    if (!tabTrigger) {
      // Fallback to default tab
      tabParam = 'system_view';
      url.searchParams.set('tab', tabParam);
      history.replaceState(null, '', url);
      tabTrigger = getTabTrigger(tabParam);
    }

    if (tabTrigger) {
      tabTrigger.click();
    }
  }

  attachCollapseRowListeners() {
    document.querySelectorAll('tr[data-bs-toggle="collapse"]').forEach(row => {
      const targetId = row.getAttribute('data-bs-target');
      const target = document.querySelector(targetId);
      if (!target) return;

      // Remove any previous listeners to avoid duplicates
      target.removeEventListener('show.bs.collapse', row._showCollapseHandler || (() => {}));
      target.removeEventListener('hide.bs.collapse', row._hideCollapseHandler || (() => {}));

      // Define handlers
      row._showCollapseHandler = function() {
        row.classList.add('no-border-bottom');
      };
      row._hideCollapseHandler = function() {
        row.classList.remove('no-border-bottom');
      };

      // Attach listeners
      target.addEventListener('show.bs.collapse', row._showCollapseHandler);
      target.addEventListener('hide.bs.collapse', row._hideCollapseHandler);
    });
  }
}
